import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ReportService } from '../../../services/report.service';
import { MatDialog } from '@angular/material/dialog';
import { AiAnalysisComponent } from './ai_analysis/ai_analysis.component';
import { NgxSpinnerService } from 'ngx-spinner';
import { finalize } from 'rxjs/operators';
// import { Signal, computed, signal } from '@angular/core';

// interface MetadataFlag {
//   key: string;
//   flag: string;
// }

// interface Metadata {
//   flags: MetadataFlag[];
//   excluded_flags: MetadataFlag[];
//   [key: string]: any;
// }

@Component({
  selector: 'app-metadata',
  templateUrl: './metadata.component.html',
  styleUrls: ['./metadata.component.scss']
})
export class MetadataComponent implements OnInit {
  @Input() metadata!: any;
  @Input() metadataUrl!: string;
  @Input() anonymous!: boolean;
  @Output() flagsChanged = new EventEmitter<number>();
  @Output() refreshRequested = new EventEmitter<void>();

  filterControl = new FormControl('flagged');
  displayedMetadata: Array<{ key: string; value: string; flag?: string }> = [];
  userName!: any;
  submissionId!: string;
  llmAnalysis!: any;
  isLoading = false;
  hasAnalysis = false;
  excludedKeys: string[] = [];
  loadingKeys: Set<string> = new Set();
  @Input() canEdit: boolean;
  @Input() author!: any;
  currentFilter: string | null = 'flagged';
  isOnIA = false;

  constructor(private reportService: ReportService, private dialog: MatDialog, private spinnerService: NgxSpinnerService) {

  }

  ngOnInit() {
    this.isOnIA = !window.location.pathname.split('/').includes('v2');

    this.userName = JSON.parse(localStorage.getItem('user'))?.name || ''
    if (!this.isOnIA) {
      this.submissionId = window.location.pathname.split('/')[2];
    } else {
      this.submissionId = window.location.pathname.split('/')[3];
    }

    this.hasAnalysis = 'llm_analysis' in this.metadata;
    if (this.hasAnalysis) {
      this.llmAnalysis = this.metadata.llm_analysis;
    }

    this.excludedKeys = this.metadata.excluded_flags?.map((flag: any) => flag.key) || [];

    if (!this.anonymous && 'author' in this.metadata) {
      const alreadyInFlags = this.metadata.flags?.some((f: any) => f.key === 'author');
      if (!alreadyInFlags) {
        const authorValue = this.metadata['author'];
        this.metadata.flags = [
          ...(this.metadata.flags || []),
          { key: 'author', flag: `Author: ${authorValue || 'Unknown'}` }
        ];
      }
    }

    this.filterControl.valueChanges.subscribe(value => {
      this.updateDisplayedMetadata(value);
    });

    this.updateDisplayedMetadata('flagged');
  }

  private updateDisplayedMetadata(filter: string | null) {
    this.currentFilter = filter;
    if (filter === 'flagged') {
      // Show only flagged items
      this.displayedMetadata = this.metadata.flags.map(flag => {
        if (!(this.anonymous && flag.key.toLowerCase() == "author")) {
          return {
            key: flag.key,
            value: this.metadata[flag.key]?.toString() || '',
            flag: flag.flag,
            isInFlags: true,
            isExcluded: this.excludedKeys.includes(flag.key)
          };
        }

      });
    } else {
      // Show all metadata items
      this.displayedMetadata = Object.entries(this.metadata)
        .map(([key, value]) => {
          const flagItem = this.metadata.flags?.find(f => f.key === key);
          return {
            key,
            value: value?.toString() || '',
            flag: flagItem?.flag,
            isInFlags: !!flagItem,
            isExcluded: this.excludedKeys.includes(key)
          };
        });
    }
    this.displayedMetadata = this.displayedMetadata.filter(item => item !== undefined);
  }

  getProperName(key: string): string {
    return key.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
  }

  async getLLMAnalysis(): Promise<void> {
    let currentLanguage = localStorage.getItem('websiteLanguage') || 'en';
    try {
      this.isLoading = true;
      const dialogRef = this.dialog.open(AiAnalysisComponent, {
        // width: '800px',
        height: '90vh',
        width: '60vw',
        ariaLabel: 'AI Analysis Results',
        ariaLabelledBy: 'ai-analysis-dialog',
        data: {
          llmAnalysis: null,
          isNewAnalysis: true,
          isLoading: true,
          author: this.author,
          submissionId: this.submissionId,
          isOnIA: this.isOnIA,
          anonymous: this.anonymous,
        }
      });

      this.reportService.getLLMAnalysis(this.author, this.submissionId, this.isOnIA, this.anonymous)
        .pipe(
          finalize(() => {
            this.isLoading = false;
          })
        )
        .subscribe({
          next: (analysis) => {
            this.llmAnalysis = analysis;
            this.hasAnalysis = true;

            // Update dialog with analysis data
            const aiAnalysisComponent = dialogRef.componentInstance;
            aiAnalysisComponent.updateDialogData({
              llmAnalysis: analysis,
              isNewAnalysis: true,
              isLoading: false,
              author: this.author,
              submissionId: this.submissionId,
              isOnIA: this.isOnIA,
              anonymous: this.anonymous,
            });
          },
          error: (error) => {
            console.error('Error getting LLM analysis:', error);
            dialogRef.close();
          }
        });
    } catch (error) {
      console.error('Error getting LLM analysis:', error);
      this.isLoading = false;
    }
  }

  openAnalysisModal() {
    this.dialog.open(AiAnalysisComponent, {
      // width: '800px',
      height: '90vh',
      width: '60vw',
      ariaLabel: 'AI Analysis Results',
      ariaLabelledBy: 'ai-analysis-dialog',
      data: {
        llmAnalysis: this.llmAnalysis,
        isLoading: false,
        isNewAnalysis: false,
        author: this.author,
        submissionId: this.submissionId,
        isOnIA: this.isOnIA,
        anonymous: this.anonymous,
      }
    })
  }

  toggleMetadataFlag(key: string, isFlag: boolean): void {
    // Find the flag in either flags or excluded_flags array
    const flagInFlags = this.metadata?.flags?.find((f: any) => f.key === key);
    const flagInExcluded = this.metadata?.excluded_flags?.find((f: any) => f.key === key) || null;

    // Initialize excluded_flags if it doesn't exist
    if (!this.metadata.excluded_flags) {
      this.metadata.excluded_flags = [];
    }

    this.excludedKeys = this.metadata.excluded_flags.map(flag => flag.key);
    this.updateDisplayedMetadata(this.filterControl.value);

    // Calculate active flags (total flags minus excluded flags)
    const activeFlags = this.metadata.flags.filter(
      (flag: any) => !this.metadata.excluded_flags.some((ef: any) => ef.key === flag.key)
    ).length;

    this.flagsChanged.emit(activeFlags);

    this.reportService.toggleMetadataFlag(
      this.submissionId,
      this.author,
      key,
      this.isInFlags(key)
    ).subscribe({
      next: (response) => {
        if (this.isInExcludedFlags(key)) {
          if (flagInExcluded) {
            this.metadata.excluded_flags = this.metadata.excluded_flags.filter(
              (f: any) => f.key !== key
            );

            // Add back to flags
            this.metadata.flags = [...this.metadata.flags, flagInExcluded];
          }
        } else {
          // Moving from flags to excluded_flags
          if (flagInFlags) {
            // Remove from flags
            this.metadata.flags = this.metadata.flags.filter(
              (f: any) => f.key !== key
            );

            // Add to excluded_flags
            this.metadata.excluded_flags = [...(this.metadata.excluded_flags || []), flagInFlags];
          }
        }
        this.updateDisplayedMetadata(this.currentFilter);

        // Emit updated active flags count after state changes
        const updatedActiveFlags = this.metadata.flags.filter(
          (flag: any) => !this.metadata.excluded_flags.some((ef: any) => ef.key === flag.key)
        ).length;
        this.flagsChanged.emit(updatedActiveFlags);
      },
      error: (error) => {
        console.error('Error toggling flag:', error);
        // Revert changes on error
        if (this.isInExcludedFlags(key)) {
          this.metadata.excluded_flags = [...this.metadata.excluded_flags, flagInFlags];
          this.metadata.flags = this.metadata.flags.filter(f => f.key !== key);
        } else {
          this.metadata.flags = [...this.metadata.flags, flagInExcluded];
          this.metadata.excluded_flags = this.metadata.excluded_flags.filter(f => f.key !== key);
        }
        this.updateDisplayedMetadata(this.filterControl.value);

        // Emit original active flags count on error
        const activeFlags = this.metadata.flags.filter(
          (flag: any) => !this.metadata.excluded_flags.some((ef: any) => ef.key === flag.key)
        ).length;
        this.flagsChanged.emit(activeFlags);
      }
    });
  }

  isInFlags(key: string): boolean {
    return this.metadata?.flags?.some((flag: any) => flag.key === key);
  }

  isInExcludedFlags(key: string): boolean {
    if (this.metadata?.excluded_flags) {
      return this.metadata.excluded_flags.some((flag: any) => flag.key === key);
    }
    return false;
  }

  isKeyLoading(key: string): boolean {
    return this.loadingKeys.has(key);
  }

  isKeyExcluded(key: string): boolean {
    return this.excludedKeys.includes(key);
  }

  shouldShowButton(key: string): boolean {
    return this.isInFlags(key) || this.isInExcludedFlags(key);
  }

  getVisibleIndex(currentItem: any, items: any[]): number {

    return items
      .filter(item =>
        item?.key !== 'flags' &&
        item?.key !== 'path' &&
        item?.key !== 'llm_analysis' &&
        item?.key !== 'excluded_flags'
      )
      .indexOf(currentItem);
  }
}
